/**
 * 공통 API 클라이언트 유틸리티
 *
 * ApiRepository에서 사용하는 표준화된 fetch 래퍼 함수
 * 에러 처리, JSON 파싱, 헤더 설정을 자동화합니다.
 */

interface ApiClientOptions extends Omit<RequestInit, 'body'> {
  body?: unknown; // JSON.stringify 자동 처리
}

interface ApiClientResponse<T> {
  data: T;
  response: Response;
}

/**
 * API 호출 래퍼 함수
 *
 * @param url - API 엔드포인트 URL
 * @param options - fetch 옵션 (body는 자동으로 JSON.stringify 처리됨)
 * @returns 파싱된 응답 데이터와 Response 객체
 * @throws Error - API 호출 실패 시 에러 메시지와 함께 throw
 *
 * @example
 * ```ts
 * const { data } = await apiClient<MyResponse>('/api/users/profile', {
 *   method: 'POST',
 *   body: { name: 'John' },
 * });
 * ```
 */
export async function apiClient<T = unknown>(
  url: string,
  options: ApiClientOptions = {},
): Promise<ApiClientResponse<T>> {
  const { body, headers, ...restOptions } = options;

  // 표준 헤더 설정
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // body가 객체면 JSON.stringify 처리
  const requestBody = body !== undefined ? JSON.stringify(body) : undefined;

  const response = await fetch(url, {
    ...restOptions,
    headers: defaultHeaders,
    body: requestBody,
  });

  // 응답이 성공이 아니면 에러 throw
  if (!response.ok) {
    let errorMessage = `API call failed: ${response.status} ${response.statusText}`;

    // 응답 본문에서 에러 메시지 추출 시도
    try {
      const errorData = await response.json();
      if (errorData?.error && typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
    } catch {
      // JSON 파싱 실패 시 기본 메시지 사용
    }

    throw new Error(errorMessage);
  }

  // 응답 본문 파싱
  let data: T;
  try {
    data = await response.json();
  } catch (error) {
    throw new Error(
      `Failed to parse response as JSON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }

  return {
    data,
    response,
  };
}
